import { useEffect, useMemo, useRef, useState } from 'react'
import { extractApiErrorMessage } from '../../../utils/apiError'
import {
  deleteSupportTicketAttachment,
  downloadSupportTicketAttachment,
  listSupportTickets,
  updateSupportTicket,
  listTicketComments,
  createTicketComment,
  getUnreadIndicators,
  listPlatformAgents,
  listPlatformTenants,
  assignTicketToAgent,
  markTicketAsRead,
} from '../api/supportTicketApi'
import {
  parseStoredUser,
  isPlatformStaff,
  isSuperAdmin,
  isPlatformAgent,
  isAssignedAgent,
} from '../../../utils/authUtils'
import { SUPPORT_TICKET_STATUS } from '../types'

const PAGE_SIZE = 10
const TAB_TO_STATUS = {
  OPEN: SUPPORT_TICKET_STATUS.OPEN,
  IN_PROGRESS: SUPPORT_TICKET_STATUS.IN_PROGRESS,
  CLOSED: SUPPORT_TICKET_STATUS.CLOSED,
  ALL: '',
}

function formatDate(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const d = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  const t = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
  return `${d}, ${t}`
}

function formatTat(createdAt, closedAt) {
  if (!createdAt) return '-'
  const start = new Date(createdAt)
  if (Number.isNaN(start.getTime())) return '-'
  const end = closedAt ? new Date(closedAt) : new Date()
  const diffMs = Math.max(0, end.getTime() - start.getTime())
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  if (days <= 0) return `${remainingHours}h`
  return `${days}d ${remainingHours}h`
}

function getCurrentUserContext() {
  try {
    const user = parseStoredUser()
    const rawId = user?.userId ?? user?.id ?? user?.user_id
    const normalizedId = Number(rawId)
    const userId = Number.isFinite(normalizedId) && normalizedId > 0 ? String(normalizedId) : '0'
    const isAdmin = Boolean(user?.isAdmin ?? user?.is_admin)
    const platformRole = user?.platformRole ?? user?.platform_role ?? null
    const isStaff = isPlatformStaff(user)
    const isSuper = isSuperAdmin(user)
    const isAgent = isPlatformAgent(user)
    return {
      userId,
      isAdmin,
      platformRole,
      isStaff,
      isSuperAdmin: isSuper,
      isPlatformAgent: isAgent,
      canManageSupport: isStaff || isAdmin,
      user,
    }
  } catch {
    return {
      userId: '0',
      isAdmin: false,
      platformRole: null,
      isStaff: false,
      isSuperAdmin: false,
      isPlatformAgent: false,
      canManageSupport: false,
      user: {},
    }
  }
}

function normalizePriority(value) {
  const normalized = String(value || '').toUpperCase()
  if (normalized === 'HIGH') return 'High'
  if (normalized === 'LOW') return 'Low'
  if (normalized === 'URGENT') return 'Urgent'
  return 'Medium'
}

function statusLabel(value) {
  return String(value || SUPPORT_TICKET_STATUS.OPEN).replaceAll('_', ' ')
}

export function useSupportTicketManagement() {
  const [tab, setTab] = useState('OPEN')
  const [priority, setPriority] = useState('')
  const [category, setCategory] = useState('')
  const [tenantId, setTenantId] = useState('')
  const [assignedToMeOnly, setAssignedToMeOnly] = useState(false)
  const [hasUnseenOnly, setHasUnseenOnly] = useState(false)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)
  const [reloadToken, setReloadToken] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [rows, setRows] = useState([])
  const [pagination, setPagination] = useState({ totalCount: 0 })
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [rowEdits, setRowEdits] = useState({})

  // Platform staff metadata (tenants & agents)
  const [tenants, setTenants] = useState([])
  const [agents, setAgents] = useState([])

  // Assign agent modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [ticketToAssign, setTicketToAssign] = useState(null)
  const [isAssigning, setIsAssigning] = useState(false)

  // Super Admin Platform Staff modal state
  const [staffModalOpen, setStaffModalOpen] = useState(false)

  // Comments state
  const [commentsByTicket, setCommentsByTicket] = useState({}) // { [ticketId]: [] }
  const [commentsLoading, setCommentsLoading] = useState({}) // { [ticketId]: bool }
  const [newCommentText, setNewCommentText] = useState({}) // { [ticketId]: string }
  const [isPostingComment, setIsPostingComment] = useState({}) // { [ticketId]: bool }
  const commentLoadedRef = useRef(new Set()) // track which ticket ids already loaded

  // Global cross-tab unread indicators
  const [unreadIndicatorsRaw, setUnreadIndicatorsRaw] = useState([])

  // Persistent read-state
  const LS_KEY = 'bridge_read_ticket_comments'
  const [readComments, setReadComments] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') } catch { return {} }
  })

  function markAsRead(ticketId, commentId) {
    if (!ticketId || !commentId) return
    setReadComments((prev) => {
      if (Number(prev[ticketId]) >= Number(commentId)) return prev
      const next = { ...prev, [ticketId]: Number(commentId) }
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)) } catch { /* storage full */ }
      window.dispatchEvent(new CustomEvent('bridge-support-comments-read', { detail: { ticketId, commentId } }))
      return next
    })
  }

  const userContext = useMemo(() => getCurrentUserContext(), [reloadToken])

  // Load active tenants & agents for platform staff
  useEffect(() => {
    if (!userContext.isStaff) return
    listPlatformTenants().then(setTenants).catch(() => setTenants([]))
    listPlatformAgents().then(setAgents).catch(() => setAgents([]))
  }, [userContext.isStaff])

  // Tenant lookup map
  const tenantMap = useMemo(() => {
    const map = new Map()
    tenants.forEach((t) => {
      map.set(Number(t.id), t.name || t.code || `Tenant #${t.id}`)
    })
    return map
  }, [tenants])

  // Agent lookup map
  const agentMap = useMemo(() => {
    const map = new Map()
    agents.forEach((a) => {
      const uid = Number(a.user_id || a.id)
      map.set(uid, a.email || `Agent #${uid}`)
    })
    return map
  }, [agents])

  useEffect(() => {
    let active = true
    async function loadTickets() {
      setIsLoading(true)
      setError('')
      setSuccess('')
      try {
        const response = await listSupportTickets({
          page,
          pageSize: PAGE_SIZE,
          status: TAB_TO_STATUS[tab],
          priority,
          category,
          tenantId: userContext.isStaff && tenantId ? Number(tenantId) : undefined,
          assignedAgentId: userContext.isStaff && assignedToMeOnly ? Number(userContext.userId) : undefined,
          dateFrom,
          dateTo,
          hasUnseenOnly: Boolean(hasUnseenOnly),
        })
        if (!active) return
        const mapped = (response.rows || []).map((ticket) => {
          const tId = Number(ticket.tenantId || 0)
          const aId = ticket.assignedAgentId != null ? Number(ticket.assignedAgentId) : null
          return {
            id: Number(ticket.id || 0),
            ticketNo: ticket.ticketNo || `BT-${String(ticket.id || '').padStart(5, '0')}`,
            subject: ticket.subject || 'No subject',
            description: ticket.description || '',
            category: String(ticket.category || 'ISSUE').replaceAll('_', ' '),
            createdOn: formatDate(ticket.createdAt),
            createdAt: ticket.createdAt || null,
            tenantId: tId,
            tenantName: tenantMap.get(tId) || (tId ? `Tenant #${tId}` : '-'),
            assignedAgentId: aId,
            assignedAgentEmail: aId ? (agentMap.get(aId) || `Agent #${aId}`) : null,
            agentAssignedAt: ticket.agentAssignedAt || null,
            requester: ticket.requesterName || 'System',
            requesterId: String(ticket.requesterId || ''),
            requesterEmail: ticket.requesterEmail || '',
            priority: normalizePriority(ticket.priority),
            rawPriority: String(ticket.priority || 'MEDIUM').toUpperCase(),
            status: String(ticket.status || SUPPORT_TICKET_STATUS.OPEN).toUpperCase(),
            statusLabel: statusLabel(ticket.status),
            attachments: Array.isArray(ticket.attachments) ? ticket.attachments : [],
            tat: formatTat(ticket.createdAt, ticket.closedAt),
            lastCommentId: ticket.lastCommentId ?? null,
            lastCommentIsStaff: ticket.lastCommentIsStaff ?? null,
            lastCommentUserId: ticket.lastCommentUserId ?? null,
            hasUnseenComments: Boolean(ticket.hasUnseenComments),
          }
        })
        setRows(mapped)
        setPagination({ totalCount: Number(response.totalCount || 0) })
        const defaults = {}
        mapped.forEach((item) => {
          defaults[item.id] = { status: item.status }
        })
        setRowEdits(defaults)
      } catch (requestError) {
        if (!active) return
        setRows([])
        setPagination({ totalCount: 0 })
        setError(extractApiErrorMessage(requestError, 'Failed to load support tickets.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }
    loadTickets()
    return () => {
      active = false
    }
  }, [
    tab,
    priority,
    category,
    tenantId,
    assignedToMeOnly,
    hasUnseenOnly,
    dateFrom,
    dateTo,
    page,
    reloadToken,
    userContext.isStaff,
    userContext.userId,
    tenantMap,
    agentMap,
  ])

  // Fetch global unread indicators for ALL statuses whenever data reloads.
  useEffect(() => {
    let active = true
    async function loadIndicators() {
      try {
        const list = await getUnreadIndicators()
        if (!active) return
        setUnreadIndicatorsRaw(Array.isArray(list) ? list : [])
      } catch {
        // silently ignore — indicators are best-effort
      }
    }
    loadIndicators()
    return () => { active = false }
  }, [reloadToken])

  async function loadComments(ticketId) {
    if (!ticketId || commentsLoading[ticketId]) return
    setCommentsLoading((prev) => ({ ...prev, [ticketId]: true }))
    try {
      const comments = await listTicketComments(ticketId)
      const list = Array.isArray(comments) ? comments : []
      setCommentsByTicket((prev) => ({ ...prev, [ticketId]: list }))
      commentLoadedRef.current.add(ticketId)
      if (list.length > 0) {
        const lastComment = list[list.length - 1]
        markAsRead(ticketId, lastComment.id)
      }
    } catch {
      setCommentsByTicket((prev) => ({ ...prev, [ticketId]: [] }))
    } finally {
      setCommentsLoading((prev) => ({ ...prev, [ticketId]: false }))
    }
  }

  async function postComment(ticketId, { reopenTicket = false } = {}) {
    const text = String(newCommentText[ticketId] || '').trim()
    if (!text || isPostingComment[ticketId]) return
    setIsPostingComment((prev) => ({ ...prev, [ticketId]: true }))
    try {
      const created = await createTicketComment(ticketId, text, { reopenTicket })
      const additions = [created, created.systemComment].filter(Boolean)
      setCommentsByTicket((prev) => ({
        ...prev,
        [ticketId]: [...(prev[ticketId] || []), ...additions],
      }))
      setNewCommentText((prev) => ({ ...prev, [ticketId]: '' }))
      markAsRead(ticketId, additions[additions.length - 1].id)
      if (created.ticketStatus === SUPPORT_TICKET_STATUS.OPEN) {
        const reopen = (ticket) => ticket?.id === ticketId
          ? { ...ticket, status: SUPPORT_TICKET_STATUS.OPEN, statusLabel: 'Open', tat: formatTat(ticket.createdAt, null) }
          : ticket
        setRows((prev) => prev.map(reopen))
        setSelectedTicket(reopen)
        setRowEdits((prev) => ({ ...prev, [ticketId]: { status: SUPPORT_TICKET_STATUS.OPEN } }))
        setUnreadIndicatorsRaw((prev) => prev.map((ticket) => ticket.id === ticketId ? { ...ticket, status: 'OPEN' } : ticket))
        setSuccess('Your feedback was sent and the ticket has been reopened.')
        setTab('OPEN')
        setPage(1)
        setReloadToken((prev) => prev + 1)
      }
      return true
    } catch (postError) {
      setError(extractApiErrorMessage(postError, 'Failed to post comment.'))
      return false
    } finally {
      setIsPostingComment((prev) => ({ ...prev, [ticketId]: false }))
    }
  }

  const statusEditOptions = useMemo(
    () => [
      { value: SUPPORT_TICKET_STATUS.OPEN, label: 'Open' },
      { value: SUPPORT_TICKET_STATUS.IN_PROGRESS, label: 'In Progress' },
      { value: SUPPORT_TICKET_STATUS.CLOSED, label: 'Closed' },
    ],
    [],
  )

  const totalPages = Math.max(1, Math.ceil((pagination.totalCount || 0) / PAGE_SIZE))

  async function saveRowUpdate(ticketId) {
    const edit = rowEdits[ticketId]
    if (!edit) return
    setIsSaving(true)
    setError('')
    setSuccess('')
    try {
      await updateSupportTicket(ticketId, { status: edit.status })
      await loadComments(ticketId)
      setSuccess('Ticket status updated successfully.')
      setReloadToken((prev) => prev + 1)
    } catch (updateError) {
      setError(extractApiErrorMessage(updateError, 'Failed to update support ticket.'))
    } finally {
      setIsSaving(false)
    }
  }

  function updateRowEdit(ticketId, key, value) {
    setRowEdits((prev) => ({
      ...prev,
      [ticketId]: {
        ...(prev[ticketId] || {}),
        [key]: value,
      },
    }))
  }

  async function handleAssignTicket(ticketId, agentUserId) {
    setIsAssigning(true)
    setError('')
    try {
      await assignTicketToAgent(ticketId, agentUserId)
      setSuccess('Ticket assigned to agent successfully.')
      setReloadToken((prev) => prev + 1)
    } catch (assignError) {
      setError(extractApiErrorMessage(assignError, 'Failed to assign ticket.'))
      throw assignError
    } finally {
      setIsAssigning(false)
    }
  }

  function openAssignModal(ticket) {
    setTicketToAssign(ticket)
    setAssignModalOpen(true)
  }

  function closeAssignModal() {
    setAssignModalOpen(false)
    setTicketToAssign(null)
  }

  async function previewAttachment(ticket, attachment) {
    const ticketId = Number(ticket?.id || 0)
    const attachmentId = String(attachment?.id || '')
    if (!ticketId || !attachmentId) return
    setError('')
    try {
      const { blob, contentType } = await downloadSupportTicketAttachment(ticketId, attachmentId)
      const typedBlob = blob instanceof Blob ? blob : new Blob([blob], { type: contentType || 'application/octet-stream' })
      const objectUrl = URL.createObjectURL(typedBlob)
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Unable to preview attachment.'))
    }
  }

  async function deleteAttachment(ticket, attachment) {
    const ticketId = Number(ticket?.id || 0)
    const attachmentId = String(attachment?.id || '')
    if (!ticketId || !attachmentId) return
    setError('')
    try {
      await deleteSupportTicketAttachment(ticketId, attachmentId)
      setReloadToken((prev) => prev + 1)
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Unable to delete attachment.'))
    }
  }

  function openDetails(row) {
    setSelectedTicket(row)
    // Mark as read in Master DB
    markTicketAsRead(row.id).catch(() => {})
    // Optimistically clear unseen flag in local state so breathing dot turns off immediately
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, hasUnseenComments: false } : r))
    )
    setUnreadIndicatorsRaw((prev) =>
      prev.map((t) => (t.id === row.id ? { ...t, hasUnseen: false } : t))
    )
    if (row.lastCommentId) {
      markAsRead(row.id, row.lastCommentId)
    }
    const cachedComments = commentsByTicket[row.id] || []
    if (!commentLoadedRef.current.has(row.id) || Number(row.lastCommentId || 0) > Number(cachedComments.at(-1)?.id || 0)) {
      loadComments(row.id)
    }
  }

  function closeDetails() {
    setSelectedTicket(null)
  }

  // Permissions helpers for rows and components
  const canAssignTicket = (ticket) => userContext.isSuperAdmin
  const canEditTicketStatus = (ticket) => {
    if (userContext.isSuperAdmin) return true
    return isAssignedAgent(ticket, userContext.user)
  }
  const canCommentOnTicket = (ticket) => {
    if (userContext.isSuperAdmin) return true
    if (isAssignedAgent(ticket, userContext.user)) return true
    return String(ticket?.requesterId) === String(userContext.userId)
  }

  // Indicator Map for View button breathing dots
  const indicatorMap = useMemo(() => {
    const ids = new Set()
    rows.forEach((row) => {
      if (row.hasUnseenComments) {
        ids.add(row.id)
      }
    })
    return ids
  }, [rows])

  // Tab indicator breathing dots
  const TAB_STATUS_VALUES = new Set(['OPEN', 'IN_PROGRESS', 'CLOSED'])
  const tabIndicatorSet = useMemo(() => {
    const statuses = new Set()
    unreadIndicatorsRaw.forEach((t) => {
      if (t.hasUnseen) {
        const s = String(t.status || '').toUpperCase()
        if (TAB_STATUS_VALUES.has(s)) statuses.add(s)
      }
    })
    return statuses
  }, [unreadIndicatorsRaw])

  const isFilterActive = useMemo(() => {
    return Boolean(
      priority ||
      category ||
      tenantId ||
      assignedToMeOnly ||
      hasUnseenOnly ||
      dateFrom ||
      dateTo
    )
  }, [priority, category, tenantId, assignedToMeOnly, hasUnseenOnly, dateFrom, dateTo])

  const resetFilters = () => {
    setPriority('')
    setCategory('')
    setTenantId('')
    setAssignedToMeOnly(false)
    setHasUnseenOnly(false)
    setDateFrom('')
    setDateTo('')
    setPage(1)
  }

  return {
    state: {
      tab,
      priority,
      category,
      tenantId,
      assignedToMeOnly,
      hasUnseenOnly,
      staffModalOpen,
      dateFrom,
      dateTo,
      page,
      totalPages,
      totalCount: pagination.totalCount || 0,
      isLoading,
      isSaving,
      isAssigning,
      error,
      success,
      rows,
      tenants,
      agents,
      assignModalOpen,
      ticketToAssign,
      statusEditOptions,
      countLabel: `${pagination.totalCount || 0} entries`,
      canManageSupport: userContext.canManageSupport,
      isStaff: userContext.isStaff,
      isSuperAdmin: userContext.isSuperAdmin,
      isPlatformAgent: userContext.isPlatformAgent,
      platformRole: userContext.platformRole,
      currentUserId: userContext.userId,
      currentTenantId: userContext.user?.tenantId ?? userContext.user?.tenant_id ?? 0,
      selectedTicket,
      rowEdits,
      commentsByTicket,
      commentsLoading,
      newCommentText,
      isPostingComment,
      indicatorMap,
      tabIndicatorSet,
      isFilterActive,
    },
    actions: {
      setTab: (value) => {
        setTab(value)
        setPage(1)
      },
      setPriority: (value) => {
        setPriority(String(value || ''))
        setPage(1)
      },
      setCategory: (value) => {
        setCategory(String(value || ''))
        setPage(1)
      },
      setTenantId: (value) => {
        setTenantId(String(value || ''))
        setPage(1)
      },
      setAssignedToMeOnly: (value) => {
        setAssignedToMeOnly(Boolean(value))
        setPage(1)
      },
      setHasUnseenOnly: (value) => {
        setHasUnseenOnly(Boolean(value))
        setPage(1)
      },
      setDateFrom: (value) => {
        setDateFrom(value)
        setPage(1)
      },
      setDateTo: (value) => {
        setDateTo(value)
        setPage(1)
      },
      resetFilters,
      setPage: (value) => setPage(Math.max(1, Math.min(totalPages, Number(value) || 1))),
      refresh: () => setReloadToken((prev) => prev + 1),
      openDetails,
      closeDetails,
      openAssignModal,
      closeAssignModal,
      openStaffModal: () => setStaffModalOpen(true),
      closeStaffModal: () => setStaffModalOpen(false),
      assignTicket: handleAssignTicket,
      updateRowEdit,
      saveRowUpdate,
      previewAttachment,
      deleteAttachment,
      setNewCommentText: (ticketId, value) => setNewCommentText((prev) => ({ ...prev, [ticketId]: value })),
      postComment,
      formatTime,
      formatDateTime,
      canAssignTicket,
      canEditTicketStatus,
      canCommentOnTicket,
    },
  }
}
